import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, ShieldCheck, Dumbbell, LockKeyhole } from "lucide-react";
import { api, errMsg, setAuth, getToken, getProfile } from "./api";
import { BigBtn, inputCls } from "./ui";

const LOGOS = [
  { src: "/assets/staff/tempa-health.png", alt: "TEMPA Health" },
  { src: "/assets/staff/pedal-pop.png", alt: "Pedal Pop" },
  { src: "/assets/staff/sculpt-pop.png", alt: "Sculpt Pop" },
  { src: "/assets/staff/recovery-pop.png", alt: "Recovery Pop" },
];

export default function Gate() {
  const nav = useNavigate();
  const [step, setStep] = useState("role");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [gateToken, setGateToken] = useState(null);
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [tpw, setTpw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const pickAdmin = async () => {
    setErr("");
    const tok = getToken();
    const prof = getProfile();
    if (tok && prof?.kind === "staff") {
      try {
        await api.get("/auth/me");
        setStep("name");
        return;
      } catch {}
    }
    setStep("password");
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/gate", { password });
      setGateToken(data.gate_token);
      setStep("name");
    } catch (ex) { setErr(errMsg(ex)); }
    setBusy(false);
  };

  const submitName = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setErr("Please enter your full name"); return; }
    setErr(""); setBusy(true);
    try {
      const headers = gateToken ? { Authorization: `Bearer ${gateToken}` } : undefined;
      const { data } = await api.post("/auth/identity", { name: name.trim(), role: "admin" }, { headers });
      setAuth(data.token, { name: data.name, role: "admin", kind: "staff" });
      nav("/staff");
    } catch (ex) { setErr(errMsg(ex)); }
    setBusy(false);
  };

  const trainerLogin = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/sched/auth/trainer", { mobile, password: tpw });
      setAuth(data.token, { name: data.name, role: "trainer", kind: "trainer", trainer_id: data.trainer_id });
      if (data.must_change_password) setStep("changepw");
      else nav("/staff");
    } catch (ex) { setErr(errMsg(ex)); }
    setBusy(false);
  };

  const changePw = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/sched/auth/trainer/change-password", { new_password: newPw });
      if (data.token) {
        const prof = getProfile();
        setAuth(data.token, prof);
      }
      nav("/staff");
    } catch (ex) { setErr(errMsg(ex)); }
    setBusy(false);
  };

  const H = ({ children }) => <h1 className="text-3xl uppercase italic" style={{ fontFamily: "Anton, sans-serif" }}>{children}</h1>;

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col" style={{ fontFamily: "Archivo, sans-serif" }}>
      <div className="flex-1 mx-auto w-full max-w-md px-6 py-10 flex flex-col justify-center">
        {step === "role" && (
          <div className="space-y-8" data-testid="role-first-screen">
            <div className="grid grid-cols-2 gap-x-8 gap-y-6 items-center justify-items-center py-2">
              {LOGOS.map((l) => <img key={l.alt} src={l.src} alt={l.alt} className="h-12 w-auto max-w-[140px] object-contain" />)}
            </div>
            <div className="text-center space-y-2">
              <H>What's your role today?</H>
              <p className="text-white/55 text-sm">One venue. One team. Choose your role to get started.</p>
            </div>
            <div className="space-y-4">
              <button data-testid="role-card-admin" onClick={pickAdmin}
                className="w-full rounded-2xl border border-white/15 bg-[#141414] p-5 text-left flex items-center gap-4 active:scale-[0.98] transition">
                <ShieldCheck size={30} className="text-[#ff2e4d]" />
                <span>
                  <span className="block text-2xl uppercase" style={{ fontFamily: "Anton, sans-serif" }}>Admin</span>
                  <span className="block text-sm text-white/50">Daily work, venue care and shift sign-off.</span>
                </span>
              </button>
              <button data-testid="role-card-trainer" onClick={() => { setErr(""); setStep("trainer"); }}
                className="w-full rounded-2xl border border-white/15 bg-[#141414] p-5 text-left flex items-center gap-4 active:scale-[0.98] transition">
                <Dumbbell size={30} className="text-[#ff2e4d]" />
                <span>
                  <span className="block text-2xl uppercase" style={{ fontFamily: "Anton, sans-serif" }}>Trainer</span>
                  <span className="block text-sm text-white/50">Class schedules, supply requests and bike reports.</span>
                </span>
              </button>
            </div>
            <div className="text-center">
              <Link to="/staff/superadmin" data-testid="superadmin-login-link"
                className="inline-flex items-center gap-1.5 text-xs text-white/35 min-h-[44px] underline underline-offset-4">
                <LockKeyhole size={12} /> Super-admin login
              </Link>
            </div>
          </div>
        )}

        {step === "password" && (
          <form onSubmit={submitPassword} className="space-y-6" data-testid="staff-password-screen">
            <button type="button" onClick={() => setStep("role")} className="text-sm text-white/45 underline underline-offset-4 min-h-[44px]">← Change role</button>
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#ff2e4d]">Admin</p>
              <H>Venue password</H>
              <p className="text-white/55 text-sm">The shared staff password for this venue.</p>
            </div>
            <div className="relative">
              <input data-testid="staff-password-input" type={show ? "text" : "password"} value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="Venue password"
                className={inputCls + " pr-14"} autoComplete="current-password" />
              <button type="button" data-testid="staff-password-toggle" aria-label="Show or hide password"
                onClick={() => setShow((v) => !v)}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-11 w-11 flex items-center justify-center text-white/50">
                {show ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {err && <p data-testid="staff-auth-error" className="text-[#ff2e4d] text-sm">{err}</p>}
            <BigBtn data-testid="staff-enter-button" type="submit" disabled={busy || !password}>{busy ? "Checking…" : "Continue"}</BigBtn>
          </form>
        )}

        {step === "name" && (
          <form onSubmit={submitName} className="space-y-6" data-testid="staff-name-screen">
            <button type="button" onClick={() => setStep("role")} className="text-sm text-white/45 underline underline-offset-4 min-h-[44px]">← Change role</button>
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#ff2e4d]">Admin</p>
              <H>What's your name?</H>
              <p className="text-white/55 text-sm">Your name is recorded with everything you sign off.</p>
            </div>
            <input data-testid="staff-name-input" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Full name" className={inputCls} autoComplete="name" />
            {err && <p data-testid="staff-auth-error" className="text-[#ff2e4d] text-sm">{err}</p>}
            <BigBtn data-testid="staff-name-continue" type="submit" disabled={busy}>{busy ? "Signing in…" : "Continue"}</BigBtn>
          </form>
        )}

        {step === "trainer" && (
          <form onSubmit={trainerLogin} className="space-y-6" data-testid="trainer-login-screen">
            <button type="button" onClick={() => setStep("role")} className="text-sm text-white/45 underline underline-offset-4 min-h-[44px]">← Change role</button>
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#ff2e4d]">Trainer</p>
              <H>Trainer sign in</H>
              <p className="text-white/55 text-sm">Use your own mobile number and password — no venue password needed. Forgot it? Contact your manager for a reset.</p>
            </div>
            <input data-testid="trainer-mobile-input" value={mobile} onChange={(e) => setMobile(e.target.value)}
              placeholder="Mobile (04xx xxx xxx)" inputMode="tel" className={inputCls} autoComplete="username" />
            <div className="relative">
              <input data-testid="trainer-password-input" type={show ? "text" : "password"} value={tpw}
                onChange={(e) => setTpw(e.target.value)} placeholder="Password" className={inputCls + " pr-14"} autoComplete="current-password" />
              <button type="button" aria-label="Show or hide password" onClick={() => setShow((v) => !v)}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-11 w-11 flex items-center justify-center text-white/50">
                {show ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {err && <p data-testid="trainer-login-error" className="text-[#ff2e4d] text-sm">{err}</p>}
            <BigBtn data-testid="trainer-login-button" type="submit" disabled={busy || !mobile || !tpw}>{busy ? "Signing in…" : "Sign in"}</BigBtn>
          </form>
        )}

        {step === "changepw" && (
          <form onSubmit={changePw} className="space-y-6" data-testid="trainer-changepw-screen">
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#ff2e4d]">Trainer</p>
              <H>Set your new password</H>
              <p className="text-white/55 text-sm">You signed in with a temporary password — choose your own (8+ characters).</p>
            </div>
            <input data-testid="trainer-newpw-input" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
              placeholder="New password" className={inputCls} autoComplete="new-password" />
            {err && <p className="text-[#ff2e4d] text-sm">{err}</p>}
            <BigBtn data-testid="trainer-newpw-button" type="submit" disabled={busy || newPw.length < 8}>Save & continue</BigBtn>
          </form>
        )}
      </div>
      <p className="text-center text-white/25 text-xs pb-6">Internal staff system · <a className="underline" href="/">tempa.health</a></p>
    </div>
  );
}
